/** @jsxImportSource @emotion/react */
'use memo'

import styled from '@emotion/styled'
import { Link } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { ORG_REGISTRATION_COMPETITION_PLACEHOLDER } from '@sportninja/common/features/organization/registration/constants'
import type { OrgRegistration } from '@sportninja/common/features/organization/registration/types'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { paragraphSmall, weightSemiBold } from 'src/components/css'

interface Props {
  registration: OrgRegistration
}

/**
 * The competition's logo and name. The name opens the competition in a new
 * tab, as on the card, so the list keeps its sort and expanded rows.
 */
const CompetitionCell = ({ registration }: Props) => {
  'use memo'

  return (
    <Wrapper>
      <Logo
        src={
          resolveAssetUrl(registration.image) ??
          ORG_REGISTRATION_COMPETITION_PLACEHOLDER
        }
        alt=''
      />
      <Name
        to={`${ROUTES.SCHEDULE_ROOT}/${registration.scheduleId}`}
        target='_blank'
        rel='noopener noreferrer'
        title={registration.name}
      >
        {registration.name}
      </Name>
    </Wrapper>
  )
}

export default CompetitionCell

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  height: 100%;
`

const Logo = styled.img`
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border-radius: 8px;
  object-fit: cover;
  background: ${colors.SECONDARY_200};
`

// Two lines at most, so a long name never makes its row taller than the rest.
const Name = styled(Link)`
  ${paragraphSmall}
  ${weightSemiBold}
  min-width: 0;
  line-height: 1.3;
  white-space: normal;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  color: ${colors.WHITE};

  &:hover,
  &:focus-visible {
    color: ${colors.WHITE};
    text-decoration: underline;
  }
`
